import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  CircularProgress,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { Plus, Users, KeyRound, ShieldPlus, Building2, Power, PowerOff, Lock, X, Search } from 'lucide-react';
import partnerAdminService from '../../services/partnerAdminService';
import ApiKeyRevealDialog from '../shared/ApiKeyRevealDialog';
import { useUser } from '../../context/UserContext';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { ROLE_NAMES } from '../../constants/permissions';
import { PARTNER_ADMIN_COPY, ROLE_COPY, TENANCY_COPY } from '../../constants/partnerCopy';
import { describeApiError, mapApiError } from '../../utils/apiError';
import PaginationBar from '../PlatformAdmin/PaginationBar';
import styles from '../PlatformAdmin/PlatformAdmin.module.scss';
import usageStyles from '../shared/UsageAnalytics/UsageAnalyticsPanel.module.scss';

const PAGE_SIZE = 50;
const ALL = 'all';
const DIRECT = 'direct'; // create form: no company_id ⇒ direct user

const EMPTY_FORM = { email: '', first_name: '', last_name: '', company: DIRECT, role_ids: [] };

const fullName = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User ${u.id}`;

const normalizeRoles = (data) => (Array.isArray(data) ? data : data?.roles || []);

// partner_admin first, then the other system roles, then custom roles by name.
const sortRoles = (roles) =>
  [...roles].sort((a, b) => {
    if (a.name === ROLE_NAMES.PARTNER_ADMIN) return -1;
    if (b.name === ROLE_NAMES.PARTNER_ADMIN) return 1;
    if (!!a.is_system !== !!b.is_system) return a.is_system ? -1 : 1;
    return (a.name || '').localeCompare(b.name || '');
  });

/**
 * The partner's users across all its companies (Partner tier guide §5.4) —
 * direct users (the partner's own staff) and every client company's users.
 * Direct users can hold partner_admin, never company_admin; client-company
 * users the reverse. A partner's users always belong to one of its companies.
 */
const PartnerUsersPanel = ({ ctx, notify, intent, onIntentConsumed }) => {
  const { user: currentUser } = useUser();
  const { companies, homeCompanyId, partnerName } = ctx;

  const [filter, setFilter] = useState(ALL);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formRoles, setFormRoles] = useState([]);
  const [formRolesFor, setFormRolesFor] = useState(null); // company value formRoles belong to
  const [formRolesLoading, setFormRolesLoading] = useState(false);
  const [tickCompanyAdmin, setTickCompanyAdmin] = useState(false);

  const [roleTarget, setRoleTarget] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [roleToAdd, setRoleToAdd] = useState('');
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveTo, setMoveTo] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [keyReveal, setKeyReveal] = useState(null);

  const userErrors = useMemo(
    () => [
      { status: 400, match: /deactivate your own account/i, message: 'You cannot deactivate your own account' },
      { status: 400, match: /last active partner admin/i, message: PARTNER_ADMIN_COPY.lastPartnerAdmin(partnerName) },
      { status: 400, match: /partner_admin can only be held/i, message: PARTNER_ADMIN_COPY.onlyDirectUsersPartnerAdmin },
      { status: 400, match: /administered with partner_admin, not company_admin/i, message: PARTNER_ADMIN_COPY.directUsersNotCompanyAdmin },
      { status: 403, match: /higher privileges/i, message: PARTNER_ADMIN_COPY.higherTierUser },
      { status: 403, match: /always belong to one of its companies/i, message: 'Users of a partner always belong to one of its companies.' },
      { status: 404, match: /not found/i, message: PARTNER_ADMIN_COPY.noLongerInPartner(partnerName) },
    ],
    [partnerName]
  );

  const isDirect = (u) => homeCompanyId != null && Number(u?.company_id) === Number(homeCompanyId);
  const isSelf = (u) =>
    !!currentUser?.email && String(u.email || '').toLowerCase() === String(currentUser.email).toLowerCase();
  const companyCell = (u) => (isDirect(u) ? 'Direct user' : ctx.companyLabel(u.company_id));

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await partnerAdminService.listUsers({
        companyId: filter === ALL ? null : filter,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setUsers(data?.users || []);
      setTotal(Number.isFinite(data?.total) ? data.total : (data?.users || []).length);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
  }, [filter, debouncedSearch, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const fail = async (err, fallback) => {
    notify(mapApiError(err, userErrors, fallback), err?.status === 404 ? 'warning' : 'error');
    if (err?.status === 404) await load();
  };

  // ── Create ──
  const openCreate = useCallback((companyValue = DIRECT, withCompanyAdmin = false) => {
    setForm({ ...EMPTY_FORM, company: companyValue });
    setTickCompanyAdmin(withCompanyAdmin);
    setCreateOpen(true);
  }, []);

  // "Add an admin for Company A" from the Companies tab (§5.2).
  useEffect(() => {
    if (intent?.createForCompanyId == null) return;
    openCreate(intent.createForCompanyId, true);
    onIntentConsumed?.();
  }, [intent, openCreate, onIntentConsumed]);

  // Roles depend on the chosen company; reload whenever it changes.
  useEffect(() => {
    if (!createOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setFormRolesLoading(true);
        const data = await partnerAdminService.listRoles(form.company === DIRECT ? null : form.company);
        if (cancelled) return;
        // company_member is added by the backend — never a choice.
        setFormRoles(sortRoles(normalizeRoles(data).filter((r) => r.name !== ROLE_NAMES.COMPANY_MEMBER)));
        setFormRolesFor(form.company);
      } catch (err) {
        if (!cancelled) notify(mapApiError(err, userErrors, 'Failed to load roles.'), 'error');
      } finally {
        if (!cancelled) setFormRolesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [createOpen, form.company, notify, userErrors]);

  // Pre-tick company_admin once the new company's roles (not a previous
  // company's) have arrived.
  useEffect(() => {
    if (!tickCompanyAdmin || formRolesLoading || formRolesFor !== form.company) return;
    const admin = formRoles.find((r) => r.name === ROLE_NAMES.COMPANY_ADMIN);
    if (admin) {
      setForm((prev) => ({
        ...prev,
        role_ids: prev.role_ids.includes(admin.id) ? prev.role_ids : [...prev.role_ids, admin.id],
      }));
    }
    setTickCompanyAdmin(false);
  }, [tickCompanyAdmin, formRoles, formRolesFor, form.company, formRolesLoading]);

  const handleCreate = async () => {
    if (!form.email.trim()) return;
    try {
      setBusy(true);
      const payload = {
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        role_ids: form.role_ids,
      };
      if (form.company !== DIRECT) payload.company_id = form.company;
      const res = await partnerAdminService.createUser(payload);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      setKeyReveal({
        apiKey: res?.api_key,
        roles: res?.roles || [],
        title: 'User created',
        subtitle: `Copy ${res?.user?.email || 'the user'}'s API key now — it won't be shown again.`,
      });
      await Promise.all([load(), ctx.reloadMe()]);
    } catch (err) {
      await fail(err, 'Failed to create the user.');
    } finally {
      setBusy(false);
    }
  };

  // ── Row actions ──
  const openAssign = async (u) => {
    setRoleTarget(u);
    setRoleToAdd('');
    setRoleOptions([]);
    try {
      const data = await partnerAdminService.listRoles(isDirect(u) ? null : u.company_id);
      const held = new Set((u.roles || []).map((r) => r.id));
      setRoleOptions(sortRoles(normalizeRoles(data).filter((r) => !held.has(r.id))));
    } catch (err) {
      await fail(err, 'Failed to load roles.');
    }
  };

  const handleAssign = async () => {
    if (!roleTarget || !roleToAdd) return;
    try {
      setBusy(true);
      await partnerAdminService.assignUserRole(roleTarget.id, roleToAdd);
      notify('Role given.', 'success');
      setRoleTarget(null);
      await load();
    } catch (err) {
      await fail(err, 'Failed to give the role.');
    } finally {
      setBusy(false);
    }
  };

  const moveOptions = useMemo(() => {
    const list = [];
    if (homeCompanyId != null) list.push({ id: homeCompanyId, name: TENANCY_COPY.directUsers });
    companies.forEach((c) => list.push({ id: c.id, name: c.name }));
    return list.filter((c) => Number(c.id) !== Number(moveTarget?.company_id));
  }, [companies, homeCompanyId, moveTarget]);

  const moveLabel = moveOptions.find((c) => c.id === moveTo)?.name;

  const handleMove = async () => {
    if (!moveTarget || moveTo === '') return;
    try {
      setBusy(true);
      await partnerAdminService.updateUser(moveTarget.id, { company_id: moveTo });
      notify(`${fullName(moveTarget)} moved to ${moveLabel}.`, 'success');
      setMoveTarget(null);
      await load();
    } catch (err) {
      await fail(err, 'Failed to move the user.');
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (u, isActive) => {
    try {
      setBusy(true);
      await partnerAdminService.updateUser(u.id, { is_active: isActive });
      notify(isActive ? `${fullName(u)} reactivated.` : `${fullName(u)} deactivated.`, isActive ? 'success' : 'info');
      setConfirm(null);
      await load();
    } catch (err) {
      await fail(err, 'Failed to update the user.');
    } finally {
      setBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    if (confirm.type === 'deactivate') {
      await setActive(confirm.user, false);
      return;
    }
    try {
      setBusy(true);
      if (confirm.type === 'removeRole') {
        await partnerAdminService.removeUserRole(confirm.user.id, confirm.role.id);
        notify('Role removed.', 'info');
        setConfirm(null);
        await load();
      } else if (confirm.type === 'resetKey') {
        const res = await partnerAdminService.resetUserApiKey(confirm.user.id);
        setConfirm(null);
        setKeyReveal({
          apiKey: res?.api_key,
          title: 'API key reset',
          subtitle: `The old key for ${confirm.user.email} stopped working immediately. Copy the new key now.`,
        });
      }
    } catch (err) {
      await fail(err, 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const roleBadge = (r) => {
    if (r.name === ROLE_NAMES.PARTNER_ADMIN) {
      return { label: ROLE_COPY.partnerAdmin, className: styles.badgeAdmin, tip: ROLE_COPY.partnerAdminTooltip(partnerName) };
    }
    if (r.name === ROLE_NAMES.COMPANY_ADMIN) {
      return { label: ROLE_COPY.companyAdmin, className: styles.badgeAdmin, tip: ROLE_COPY.companyAdminTooltip };
    }
    return { label: r.name, className: r.is_system ? styles.badgeSystem : '', tip: r.description || '' };
  };

  const roleOptionText = (r) =>
    r.name === ROLE_NAMES.PARTNER_ADMIN
      ? { primary: ROLE_COPY.partnerAdmin, secondary: ROLE_COPY.partnerAdminTooltip(partnerName) }
      : r.name === ROLE_NAMES.COMPANY_ADMIN
      ? { primary: ROLE_COPY.companyAdmin, secondary: ROLE_COPY.companyAdminTooltip }
      : { primary: r.name, secondary: r.description || (r.is_system ? 'System role' : 'Custom role') };

  const chips = [
    { value: ALL, label: 'All' },
    ...(homeCompanyId != null ? [{ value: homeCompanyId, label: TENANCY_COPY.directUsers }] : []),
    ...companies.map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <>
      <Box className={styles.toolbar}>
        <TextField
          size="small"
          placeholder="Search email or name…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" startIcon={<Plus size={18} />} className={styles.primaryBtn} onClick={() => openCreate()}>
          New user
        </Button>
      </Box>

      <Box className={usageStyles.chipBar}>
        {chips.map((chip) => (
          <Tooltip
            key={chip.value}
            title={chip.value === homeCompanyId ? TENANCY_COPY.directUsersHint(partnerName) : ''}
            arrow
          >
            <button
              type="button"
              className={`${usageStyles.chip} ${filter === chip.value ? usageStyles.chipActive : ''}`}
              onClick={() => { setFilter(chip.value); setOffset(0); }}
            >
              {chip.label}
            </button>
          </Tooltip>
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : users.length === 0 ? (
        <Box className={styles.emptyState}>
          <Users className={styles.emptyIcon} size={56} />
          <Typography variant="h6">No users found</Typography>
          <Typography variant="body2">Create a user or pick a different filter.</Typography>
        </Box>
      ) : (
        <Box className={styles.tableCard}>
          {users.map((u) => {
            const self = isSelf(u);
            return (
              <Box key={u.id} className={styles.userRow} sx={{ flexWrap: 'wrap' }}>
                <Box className={styles.companyMain} sx={{ flex: '1 1 220px' }}>
                  <span className={styles.companyAvatar}>{(u.first_name || u.email || '?').charAt(0).toUpperCase()}</span>
                  <Box>
                    <span className={styles.companyName}>
                      {fullName(u)}
                      {self && <span className={styles.companyMeta}> · you</span>}
                    </span>
                    <span className={styles.companyMeta}>{u.email}</span>
                  </Box>
                </Box>

                <Box sx={{ flex: '0 0 auto', minWidth: 120 }}>
                  <span className={`${styles.badge} ${isDirect(u) ? styles.badgeSystem : ''}`}>
                    <Building2 size={12} />
                    {companyCell(u)}
                  </span>
                </Box>

                <Box className={styles.roleChips} sx={{ flex: '1 1 200px' }}>
                  {(u.roles || []).map((r) => {
                    const badge = roleBadge(r);
                    return (
                      <Tooltip key={r.id} title={badge.tip} arrow>
                        <span className={`${styles.badge} ${badge.className}`}>
                          {badge.label}
                          {!self && (
                            <X
                              size={12}
                              style={{ cursor: 'pointer' }}
                              onClick={() =>
                                setConfirm({
                                  type: 'removeRole',
                                  user: u,
                                  role: r,
                                  heading: `Remove "${badge.label}" from ${fullName(u)}?`,
                                  text:
                                    r.name === ROLE_NAMES.COMPANY_MEMBER
                                      ? "Removes the default member role. Their access becomes just their other roles ∩ their company's features."
                                      : `${fullName(u)} loses this role immediately.`,
                                  action: 'Remove role',
                                })
                              }
                            />
                          )}
                        </span>
                      </Tooltip>
                    );
                  })}
                </Box>

                <span className={`${styles.statusPill} ${u.is_active ? styles.active : styles.inactive}`}>
                  {u.is_active ? 'Active' : 'Inactive'}
                </span>

                <Box className={styles.actionsCell} sx={{ minWidth: 150 }}>
                  {!self && (
                    <>
                      <Tooltip title="Give role">
                        <IconButton size="small" className={styles.subtleBtn} onClick={() => openAssign(u)}>
                          <ShieldPlus size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move to…">
                        <IconButton size="small" className={styles.subtleBtn} onClick={() => { setMoveTarget(u); setMoveTo(''); }}>
                          <Building2 size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset API key">
                        <IconButton
                          size="small"
                          className={styles.subtleBtn}
                          onClick={() =>
                            setConfirm({
                              type: 'resetKey',
                              user: u,
                              heading: `Reset the API key for ${u.email}?`,
                              text: 'The current key stops working immediately. The new key is shown once.',
                              action: 'Reset key',
                            })
                          }
                        >
                          <KeyRound size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={u.is_active ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size="small"
                          className={styles.dangerIcon}
                          disabled={busy}
                          onClick={() =>
                            u.is_active
                              ? setConfirm({
                                  type: 'deactivate',
                                  user: u,
                                  heading: `Deactivate ${fullName(u)}?`,
                                  text: PARTNER_ADMIN_COPY.deactivateUser(fullName(u)),
                                  action: 'Deactivate',
                                })
                              : setActive(u, true)
                          }
                        >
                          {u.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {total > 0 && (
        <PaginationBar total={total} limit={PAGE_SIZE} offset={offset} loading={loading} unit="users" onOffsetChange={setOffset} />
      )}

      {/* Create user (§5.4) */}
      <Dialog open={createOpen} onClose={() => !busy && setCreateOpen(false)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>New user</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus label="Email" type="email" required fullWidth variant="outlined"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={styles.formField}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First name" fullWidth variant="outlined"
              value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className={styles.formField}
            />
            <TextField
              label="Last name" fullWidth variant="outlined"
              value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className={styles.formField}
            />
          </Box>
          <FormControl fullWidth>
            <InputLabel>Company</InputLabel>
            <Select
              label="Company"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value, role_ids: [] })}
            >
              <MenuItem value={DIRECT}>{TENANCY_COPY.directUserOption(partnerName)}</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Roles</InputLabel>
            <Select
              multiple
              label="Roles"
              value={form.role_ids}
              onChange={(e) => setForm({ ...form, role_ids: e.target.value })}
              input={<OutlinedInput label="Roles" />}
              renderValue={(ids) =>
                formRoles.filter((r) => ids.includes(r.id)).map((r) => roleOptionText(r).primary).join(', ')
              }
              disabled={formRolesLoading}
            >
              {formRoles.length === 0 && <MenuItem disabled>{formRolesLoading ? 'Loading roles…' : 'No roles available'}</MenuItem>}
              {formRoles.map((r) => {
                const text = roleOptionText(r);
                return (
                  <MenuItem key={r.id} value={r.id}>
                    <Checkbox checked={form.role_ids.includes(r.id)} />
                    <ListItemText primary={text.primary} secondary={text.secondary} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Every user gets the default member role automatically.
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setCreateOpen(false)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={busy || !form.email.trim()} className={styles.primaryBtn}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Create user'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Give role */}
      <Dialog open={!!roleTarget} onClose={() => !busy && setRoleTarget(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Give role</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Give a role to {roleTarget?.email}.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={roleToAdd} onChange={(e) => setRoleToAdd(e.target.value)}>
              {roleOptions.length === 0 && <MenuItem disabled>No roles available to add</MenuItem>}
              {roleOptions.map((r) => {
                const text = roleOptionText(r);
                return (
                  <MenuItem key={r.id} value={r.id}>
                    <ListItemText primary={text.primary} secondary={text.secondary} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setRoleTarget(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={busy || !roleToAdd} className={styles.primaryBtn}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Give role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move to… (no "remove from company" — §5.4) */}
      <Dialog open={!!moveTarget} onClose={() => !busy && setMoveTarget(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Move {moveTarget && fullName(moveTarget)}</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <FormControl fullWidth>
            <InputLabel>Move to</InputLabel>
            <Select label="Move to" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
              {moveOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {moveTarget && moveLabel && (
            <Box className={styles.noteBanner}>
              <span>{PARTNER_ADMIN_COPY.moveUser(fullName(moveTarget), moveLabel)}</span>
            </Box>
          )}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setMoveTarget(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleMove} disabled={busy || moveTo === ''} className={styles.primaryBtn}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm (deactivate / remove role / reset key) */}
      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>{confirm?.action}</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteHeading}>{confirm?.heading}</Typography>
          <Typography className={styles.deleteText}>{confirm?.text}</Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setConfirm(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button
            variant="contained"
            onClick={runConfirm}
            disabled={busy}
            className={styles.dangerButton}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {busy ? 'Working...' : confirm?.action}
          </Button>
        </DialogActions>
      </Dialog>

      <ApiKeyRevealDialog
        open={!!keyReveal}
        apiKey={keyReveal?.apiKey}
        roles={keyReveal?.roles}
        title={keyReveal?.title}
        subtitle={keyReveal?.subtitle}
        onClose={() => setKeyReveal(null)}
      />
    </>
  );
};

export default PartnerUsersPanel;
